import type { HostContext, Logger } from '../@types';
import AdobeNotDefOtf from '../../assets/ccsearch/AND-Regular.otf';
import { getErrorMessage } from '../utils/getErrorMessage';
import { getLogger } from '../utils/SearchLogger';

let TQFont: any;
let TQFontFamily: any;
let TQInitParams: any;
let TQWeb: any;
let tqtypes: any;
export class FontService {
	static async loadDependencies(): Promise<void> {
		const module = await import(/* webpackChunkName: "coretech" */ '@coretech/typequestweb');
		TQFont = module.TQFont;
		TQFontFamily = module.TQFontFamily;
		TQInitParams = module.TQInitParams;
		TQWeb = module.TQWeb;
		tqtypes = module.tqtypes;
	}

	static tqWeb: typeof TQWeb;
	static init: typeof TQInitParams;
	static logger: Logger;
	static fontsStatusPromise: Promise<typeof tqtypes.StatusCode>;
	private readonly _needsStart: boolean;

	constructor(context: HostContext) {
		const { hostInfo, imsInfo, locale } = context;
		if (FontService.tqWeb) {
			FontService.tqWeb.updateAuthParams(imsInfo.adobeId, imsInfo.accessToken);
			this._needsStart = false;
		} else {
			FontService.logger = getLogger();
			const authParams: typeof tqtypes.AuthParams = new tqtypes.AuthParams(
				hostInfo.apiKey,
				imsInfo.adobeId,
				imsInfo.accessToken,
			);
			authParams.isStaging = context.environment !== 'prod';
			authParams.surfaceKey = hostInfo.surface;
			authParams.clientKey = hostInfo.client;
			FontService.init = new TQInitParams(authParams);
			FontService.init.runtimeMode = tqtypes.RuntimeMode.ALL; // to tell that we want to render ui with data and enable caching etc.
			FontService.init.appEntitlement = tqtypes.AppEntitlement.FREEMIUM; // to tell that current app is free
			// FontService.init.watchFontUpdates = []; // to stop polling
			FontService.init.advancedServiceSearch = true; // to mimic adobe fonts
			// to view variable fonts
			FontService.init.additionalFontTechnologies = new Set<typeof tqtypes.AdditionalFontTechnologies>([
				tqtypes.AdditionalFontTechnologies.VARIABLE,
			]);
			TQInitParams.setLanguage(FontService.toTQLanguage(locale));
			FontService.tqWeb = new TQWeb(FontService.init);
			this._needsStart = true;
		}
	}

	async initAsync(): Promise<void> {
		if (this._needsStart) {
			await FontService.startTQWeb();
		} else {
			await FontService.togglePollingSettings(false);
		}
	}

	static async startTQWeb(): Promise<void> {
		FontService.fontsStatusPromise = new Promise((resolve, reject) => {
			FontService.init.statusUpdateCallback = (
				statusResult: typeof tqtypes.StatusCode,
				error: typeof tqtypes.Error,
			) => {
				if (statusResult !== tqtypes.StatusCode.DONE) {
					FontService.logger?.error('FontService', 'error while initiating tqweb', {
						error,
					});
					reject('error while initiating tqweb');
				}
				resolve(statusResult);
				return true;
			};
		});

		await FontService.tqWeb.start();
	}

	static toTQLanguage(locale: string): typeof tqtypes.Language {
		return locale.length > 1 ? (locale.substring(0, 2) as typeof tqtypes.Language) : tqtypes.Language.Default;
	}
	async searchFonts(query: string): Promise<(typeof TQFontFamily)[]> {
		try {
			const results = [];
			const status = await FontService.fontsStatusPromise;
			if (status === tqtypes.StatusCode.DONE) {
				for await (const family of FontService.tqWeb.searchFonts(query)) {
					if (results.length >= 10) {
						break;
					}
					results.push(family);
				}
			}
			return results;
		} catch (err) {
			getLogger().error('FontService', `Error while searching font for query : ${query}`, getErrorMessage(err));
			return [];
		}
	}

	static async registerFont(font: typeof TQFont): Promise<typeof TQFont> {
		const unicodeText = FontService.getFontText(font);
		const identifier = new Set<typeof tqtypes.TQFontIdentifer>([
			{
				postscriptName: font.postscriptName,
				fontId: font.fontId,
				unicodes: unicodeText.outputString + unicodeText.initialString,
			},
		]);

		const promises = await FontService.tqWeb.findFontsAsync(identifier, tqtypes.OperationMode.DATA);
		const resultMapEntries = Array.from(await promises.entries());
		// @ts-expect-error error due to making it async
		const entry = await resultMapEntries.find(([identifier]) => identifier.fontId === font.fontId);
		const result = await entry[1];
		if (result.error && result.error.errCode !== 0) {
			throw result.error;
		}

		const resultFont = result.font;
		const fontFace: FontFace = new globalThis.FontFace(resultFont.postscriptName, resultFont.fontBuffer);
		await fontFace.load();
		document.fonts.add(fontFace);
		return resultFont;
	}

	static isStringSupported(font: typeof TQFont, text: string): boolean {
		return text.split('').every((char) => font.characterSet.has(char.charCodeAt(0)));
	}

	static getFontText(font: typeof TQFont): { initialString: string; outputString: string } {
		return {
			// eslint-disable-next-line new-cap
			initialString: tqtypes.LanguageTwoCharPreviewString(font.script),
			// eslint-disable-next-line new-cap
			outputString: tqtypes.LanguagePreviewString(font.script),
		};
	}

	static getActiveUsersForFontFamily(family: typeof TQFontFamily): number {
		const activeFonts = family.getEntries().filter((font) => font.isActiveForUser === true);
		return activeFonts.length;
	}

	static registerTofuBoxes(): void {
		try {
			const font = new FontFace('adobe-notdef', `url(${AdobeNotDefOtf})`);
			font.load().then(() => {
				document.fonts.add(font);
			});
		} catch (error) {
			getLogger().error(
				'FontService',
				'FontPicker: fallback font could not be registered',
				getErrorMessage(error),
			);
		}
	}

	static getViewAllUrl(query: string): string {
		try {
			const viewAllUrl = this.tqWeb.getSearchQueryUrlForAdobeFonts(query);
			return viewAllUrl;
		} catch (error) {
			getLogger().error(
				'FontService',
				`Error in getting view all url for the query : ${query} in fonts`,
				getErrorMessage(error),
			);
		}
	}

	static async togglePollingSettings(polling: boolean): Promise<void> {
		try {
			// it true then polling stops else starts
			await FontService?.tqWeb?.toggleSelectionsPolling(polling);
		} catch (error) {
			getLogger().error(
				'FontService',
				'Error in togglePolling setting function in fontService',
				getErrorMessage(error),
			);
		}
	}
}

export { TQFont, TQFontFamily, tqtypes };
